import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SoecoButton } from "../../components/SoecoButton";
import { CompanyContext } from "../../context/CompanyContext";
import { useAddCompany, CreateCompany } from "../../hooks/companyHooks";
import { ErrorRecord } from "../../types";
import styled from "styled-components";
import { SoecoHeader } from "../../components/SoecoHeader";
import { useSnackBar } from "../../context/SnackbarContext";
import Div100vh from "react-div-100vh";
import { EmployeeContext } from "../../context/EmployeeContext";
import { getRolePriority } from "../../utils/util";
import AddIcon from "@mui/icons-material/Add";
import { isValidEmail } from "../../utils/util";
import AddEmailDialog from "../../components/AddEmailDialog";
import { ChevronDown } from "lucide-react";
import { useGetCollaborations } from "../../hooks/collaborationHooks";

type CreateCompanyForm = {
    name: string;
    canCreateSubcompany?: boolean;
    createdBy?: string;
    emailaddresses?: string[];
    accessMarketplace?: boolean;
    collaboration?: string;
};

export const CreateCompanyPage = () => {
    const { company } = useContext(CompanyContext);
    const mutation = useAddCompany();
    const navigate = useNavigate();
    const snackBar = useSnackBar();
    const { employee } = useContext(EmployeeContext);
    const { data: collaborations } = useGetCollaborations();
    const [form, setForm] = useState<CreateCompanyForm>({
        name: "",
        canCreateSubcompany: false,
        createdBy: employee.companyId,
        emailaddresses: [],
        accessMarketplace: false,
        collaboration: "",
    });

    const [error, setError] = useState<boolean>(false);
    const [errors, setErrors] = useState<ErrorRecord<CreateCompanyForm>>({});
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [isAddEmailDialogOpen, setIsAddEmailDialogOpen] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [selected, setSelected] = useState("");

    const transformFormData = (form: CreateCompanyForm): CreateCompany => ({
        name: form.name,
        canCreateSubcompany: form.canCreateSubcompany,
        createdBy: form.createdBy,
        emailaddresses: form.emailaddresses,
        accessMarketplace: form.accessMarketplace,
        collaboration: form.collaboration || null,
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
        setDisableButton(false);
        setError(false);
    };

    const isValidForm = () => {
        let valid = true;
        const newErrors: ErrorRecord<CreateCompanyForm> = {};

        if (form.name.length < 1) {
            valid = false;
            newErrors.name = "Name cannot be empty";
        }
        if (!valid) {
            setErrors(newErrors);
            setError(true);
            return false;
        } else {
            setErrors({});
            setError(false);
            return true;
        }
    };
    const handleChangeEmails = (email: string) => {
        const existingEmailIndex = form.emailaddresses?.findIndex(
            existingEmail => existingEmail === email,
        );
        if (existingEmailIndex !== -1) {
            setForm({
                ...form,
                emailaddresses: form.emailaddresses?.filter(
                    (existingEmail, index) => index !== existingEmailIndex,
                ),
            });
        } else {
            setForm({
                ...form,
                emailaddresses: [...(form.emailaddresses || []), email],
            });
        }
        setDisableButton(false);
        setError(false);
    };
    const handleAddEmailClick = () => {
        setIsAddEmailDialogOpen(true);
    };

    const handleCloseAddEmailDialog = () => {
        setIsAddEmailDialogOpen(false);
        setNewEmail("");
    };

    const handleSaveNewEmail = () => {
        if (newEmail.trim() === "") return;
        setForm(prev => ({
            ...prev,
            emailaddresses: [...(prev.emailaddresses || []), newEmail.trim()],
        }));

        handleCloseAddEmailDialog();
    };

    const onChangeSelected = (newId: string) => {
        // setSelected(newId);
        // onChange(newId);
    };

    const handleSubmit = () => {
        if (isValidForm()) {
            mutation.mutate(
                { company: transformFormData(form) },
                {
                    onError: error => {
                        if (error instanceof Error) {
                            snackBar.showSnackBar(error.message, "error");
                            setDisableButton(true);
                        }
                    },
                    onSuccess: () => {
                        snackBar.showSnackBar("Company created successfully", "success");
                        navigate(-1);
                    },
                },
            );
        }
    };

    return (
        <CreateCompanyPageWrapper>
            <SoecoHeader>Create Company</SoecoHeader>
            <CreateCompanyWrapper>
                <Form>
                    {company && (
                        <TextField
                            disabled={true}
                            variant="outlined"
                            defaultValue={company?.name}
                        />
                    )}
                    <TextField
                        name="name"
                        label="Name"
                        autoComplete="off"
                        error={error && errors.name ? true : false}
                        helperText={error && errors.name}
                        variant="outlined"
                        value={form.name}
                        onChange={handleChange}
                    />
                    {getRolePriority(employee.role) === 2 ||
                    getRolePriority(employee.role) === 3 ||
                    getRolePriority(employee.role) === 4 ? (
                        <></>
                    ) : (
                        <FormControlLabel
                            value={"end"}
                            label="Allow subcompany creation"
                            control={
                                <Checkbox
                                    checked={form.canCreateSubcompany}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            canCreateSubcompany: e.target.checked,
                                        }))
                                    }
                                />
                            }
                        />
                    )}
                    {employee && employee.role === "superAdmin" && (
                        <FormControlLabel
                            value={"end"}
                            label="Allow access to Internal Marketplace"
                            control={
                                <Checkbox
                                    checked={!!form.accessMarketplace}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            accessMarketplace: e.target.checked,
                                        }))
                                    }
                                />
                            }
                        />
                    )}

                    {/* <InputLabel
                        id="filter-company"
                        sx={{
                            fontFamily: "'Yllw Sans a1 241219', sans-serif",
                            fontWeight: "400",
                            color: "#8c8c8c",
                        }}
                    >
                        Acting Company
                    </InputLabel> */}

                    {form.accessMarketplace && (
                        <>
                            <Typography>Collaboration sphere</Typography>
                            <Select
                                // labelId="filter-company"
                                // label="Acting Company"
                                value={selected}
                                onChange={event => {
                                    setForm(prev => ({
                                        ...prev,
                                        collaboration: event.target.value,
                                    }));
                                    setSelected(event.target.value);
                                }}
                                IconComponent={ChevronDown}
                                sx={{
                                    fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                    fontSize: "16px",
                                    fontWeight: "400",
                                }}
                            >
                                {collaborations?.map(company => (
                                    <MenuItem key={company.id} value={company.id}>
                                        {company.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </>
                    )}
                    {getRolePriority(employee.role) === 0 && (
                        <>
                            <Divider />
                            <Typography
                                id="email-receivers-label"
                                style={{ fontWeight: 600, marginBottom: "8px" }}
                                aria-label="email-recievers-label"
                            >
                                Email Recievers
                            </Typography>
                            <div role="group" aria-labelledby="email-receivers-label">
                                {form?.emailaddresses?.map((email: string, index: number) => (
                                    <FormControlLabel
                                        key={index}
                                        control={
                                            <Checkbox
                                                onChange={() => handleChangeEmails(email)}
                                                checked={
                                                    form.emailaddresses?.includes(email) || false
                                                }
                                            />
                                        }
                                        label={email}
                                    />
                                ))}
                            </div>

                            <AddEmailDialog
                                isAddEmailDialogOpen={isAddEmailDialogOpen}
                                handleCloseAddEmailDialog={handleCloseAddEmailDialog}
                                newEmail={newEmail}
                                setNewEmail={setNewEmail}
                                handleSaveNewEmail={handleSaveNewEmail}
                            />
                            <IconButton
                                onClick={handleAddEmailClick}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignSelf: "flex-start",
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </>
                    )}
                </Form>
            </CreateCompanyWrapper>
            <ActionButtons>
                <SoecoButton fullWidth size="large" color="secondary" onClick={() => navigate(-1)}>
                    Back
                </SoecoButton>
                <SoecoButton
                    fullWidth
                    size="large"
                    disabled={mutation.isPending || disableButton}
                    color="primary"
                    className="btnLoginCreate"
                    onClick={handleSubmit}
                >
                    Create
                </SoecoButton>
            </ActionButtons>
        </CreateCompanyPageWrapper>
    );
};

const CreateCompanyPageWrapper = styled(Div100vh)`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
`;

const CreateCompanyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 95%;
    height: 70%;
    align-content: center;
    gap: 2rem;
    overflow: scroll;
    padding-top: 10px;
`;

const Form = styled(FormControl)`
    display: flex;
    flex-direction: column;
    align-content: center;
    width: 95%;
    gap: 1rem;
`;

const ActionButtons = styled.div`
    display: flex;
    width: 90%;
    gap: 2rem;
    position: fixed;
    bottom: 2rem;
`;
